/* Administration.
   ------------------------------------------------------------------------
   OWNED BY ITEM 6 OF docs/plan/00-prestavba-2026-08.md.

   This page holds no password, no digest and no secret of any kind. Whether
   somebody is an administrator is decided by the server — /api/auth/me answers
   from a session, and every write is refused to anyone else. A browser cannot
   keep a secret, so it is not asked to keep one.
   ------------------------------------------------------------------------ */

import { useCallback, useEffect, useState } from "react";
import { api, can, type Me } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { SignIn } from "./SignIn";
import { Switches } from "./Switches";
import { Spend } from "./Spend";
import { Accounts } from "./Accounts";
import { Media } from "./Media";
import { Content } from "./Content";

/* Each tab and the capability that opens it. The server decides the same thing
   again at every route; this only keeps the page from offering four tabs and
   refusing three of them. */
type Tab = "switches" | "content" | "media" | "spend" | "accounts";
const NEEDS: [Tab, string, string][] = [
  ["switches", "switches", "admin.tab.switches"],
  ["content", "content", "admin.tab.content"],
  ["media", "media", "admin.tab.media"],
  ["spend", "spend", "admin.tab.spend"],
  ["accounts", "accounts", "admin.tab.accounts"],
];

export function AdminPage() {
  const { ui } = useI18n();
  const [me, setMe] = useState<Me | null>(null);
  const [checked, setChecked] = useState(false);
  const [tab, setTab] = useState<Tab | null>(null);

  const load = useCallback(() => {
    api<Me>("/auth/me")
      .then(setMe)
      .catch(() => setMe(null))
      .finally(() => setChecked(true));
  }, []);

  useEffect(load, [load]);

  /* Nothing at all until the server has answered. Flashing the sign-in form at
     someone who is already signed in reads as having been logged out. */
  if (!checked) return null;
  if (!me) return <SignIn onSignedIn={setMe} />;

  /* The session is dropped here whatever the server said.
     A logout that failed used to throw into nothing: the page went on showing
     the administration, so the one visible fact was that pressing the button
     did not work. Forgetting who is signed in is the half this page can do on
     its own, and it is the half that matters on a borrowed laptop — the cookie
     is HttpOnly and outlives the tab, so the next request finds out the truth
     either way. */
  async function signOut() {
    try {
      await api<void>("/auth/logout", { method: "POST" });
    } finally {
      setMe(null);
    }
  }

  const tabs = NEEDS.filter(([, capability]) => can(me, capability));
  /* A workshop account holds nothing but "generate" and has no page here. It
     says so rather than showing an empty frame. */
  if (!tabs.length) {
    return (
      <div className="catalog adm">
        <header className="cat-hero">
          <h1>{ui("admin.title")}</h1>
          <p className="lead">{ui("admin.nothingHere")}</p>
        </header>
      </div>
    );
  }
  const shown = tab && tabs.some(([id]) => id === tab) ? tab : tabs[0]![0];

  return (
    <div className="catalog adm">
      <header className="cat-hero">
        <div className="eyebrow">{ui("admin.eyebrow")}</div>
        <h1>{ui("admin.title")}</h1>
        <p className="lead">
          {ui("admin.who")} <strong>{me.username}</strong>
          {" · "}{ui(`role.${me.role}`)}
          {" · "}
          <button type="button" className="adm-link" onClick={() => void signOut()}>
            {ui("admin.signOut")}
          </button>
        </p>
      </header>

      <nav className="adm-tabs">
        {tabs.map(([id, , label]) => (
          <button key={id} type="button" className={"adm-tab" + (shown === id ? " on" : "")}
                  onClick={() => setTab(id)}>
            {ui(label)}
          </button>
        ))}
      </nav>

      {shown === "switches" ? <Switches me={me} /> : null}
      {shown === "content" ? <Content me={me} /> : null}
      {shown === "media" ? <Media me={me} /> : null}
      {shown === "spend" ? <Spend /> : null}
      {shown === "accounts" ? <Accounts /> : null}
    </div>
  );
}
